import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { MENU_ITEMS } from '../../data/menu.data';
import { DateToMomentPipe } from '../../pipes/date-to-moment.pipe';
import { AuthService } from '../../services/auth.service';
import { SidebarMode } from '../side-bar/side-bar.component';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';

/** Titulo + icono de la ruta activa. */
interface ICrumb {
  title: string;
  icon: string;
}

/**
 * Barra superior fija. Es superficie de PRIMER NIVEL (lleva backdrop-filter).
 *
 * Muestra el titulo de la ruta activa (el mismo que resuelve `TitleStrategy`),
 * el conmutador de tema y el menu de perfil.
 */
@Component({
  selector: 'app-main-header',
  imports: [DateToMomentPipe, ThemeToggleComponent],
  templateUrl: './main-header.component.html',
  styleUrl: './main-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-rail]': 'mode() === "rail"',
    '[class.is-overlay]': 'mode() === "overlay"',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'closeProfile()',
  },
})
export class MainHeaderComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly mode = input<SidebarMode>('full');

  /** Pide abrir el cajon de navegacion (solo movil). */
  readonly menuToggle = output<void>();

  protected readonly user = this.authService.currentUser;
  protected readonly profileOpen = signal(false);
  protected readonly signingOut = signal(false);

  /** Se recalcula el crumb en cada navegacion terminada. */
  private readonly navigation = toSignal(
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    { initialValue: null },
  );

  protected readonly crumb = computed<ICrumb>(() => {
    // Dependencia explicita: el estado del router no es reactivo por si solo.
    this.navigation();

    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let title = '';
    while (snapshot) {
      title = snapshot.title ?? title;
      snapshot = snapshot.firstChild;
    }

    return {
      title: title || 'Consola',
      icon: this.resolveIcon(this.router.url),
    };
  });

  protected readonly initials = computed(() => {
    const user = this.user();
    if (!user) return '?';

    const letters = `${user.names.charAt(0)}${user.lastNames.charAt(0)}`.trim();
    return (letters || user.email.charAt(0)).toUpperCase();
  });

  protected toggleProfile(): void {
    this.profileOpen.update((open) => !open);
  }

  protected closeProfile(): void {
    this.profileOpen.set(false);
  }

  protected async signOut(): Promise<void> {
    if (this.signingOut()) return;

    this.signingOut.set(true);
    this.closeProfile();
    try {
      await this.authService.signOut();
    } finally {
      this.signingOut.set(false);
    }
  }

  /** Cierra el menu de perfil al pulsar fuera de la barra. */
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.profileOpen()) return;
    if (this.elementRef.nativeElement.contains(event.target as Node)) return;
    this.closeProfile();
  }

  /** Icono del menu cuya ruta case mejor (mas larga) con la URL actual. */
  private resolveIcon(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    let icon = 'grid-1x2';
    let bestLength = -1;

    for (const item of MENU_ITEMS) {
      const route = item.route;
      if (!route) continue;

      const matches = item.exact ? path === route : path === route || path.startsWith(`${route}/`);
      if (matches && route.length > bestLength) {
        bestLength = route.length;
        icon = item.icon ?? icon;
      }
    }

    return icon;
  }
}
